/*
  Cart (snippets/zp-cart.liquid + the cart page, sections/cart.liquid).

  Header: the briefcase + count in the bar, the $0.00 total + mini cart in
  the subheader. Opens and closes the mini cart, and changes quantities
  without reloading. After every change the header pieces (and the cart
  page's list, when you're on it) are redrawn from their sections through
  Shopify's section rendering, so counts, totals and lines always match.

  Anything that changes the cart elsewhere (the product page's ADD TO ORDER)
  fires `zp:cart-change`; pass `{ detail: { bump: true } }` for the little
  pop on the count and total, or `{ detail: { open: true } }` to open the
  mini cart.
*/
(() => {
  let open = false;

  const cart = () => document.querySelector('[data-zp-cart]');
  const toggle = () => document.querySelector('[data-zp-cart-toggle]');
  const headerId = () => document.querySelector('[data-zp-cart-part]')?.dataset.sectionId;
  const pageId = () => document.querySelector('[data-zp-cart-page]')?.dataset.sectionId;

  const setOpen = (value) => {
    open = value;
    const panel = document.querySelector('[data-zp-minicart]');
    if (panel) panel.hidden = !value;
    toggle()?.setAttribute('aria-expanded', String(value));
  };

  // Swap in freshly rendered pieces, keeping the mini cart open if it was.
  const render = (sections, { bump = false } = {}) => {
    let swapped = false;
    const header = sections[headerId()];
    if (header) {
      const fresh = new DOMParser().parseFromString(header, 'text/html');
      document.querySelectorAll('[data-zp-cart-part]').forEach((current) => {
        const replacement = fresh.querySelector(`[data-zp-cart-part="${current.dataset.zpCartPart}"]`);
        if (!replacement) return;
        current.replaceWith(replacement);
        swapped = true;
      });
    }
    const page = sections[pageId()];
    if (page) {
      const replacement = new DOMParser().parseFromString(page, 'text/html').querySelector('[data-zp-cart-page]');
      const current = document.querySelector('[data-zp-cart-page]');
      if (replacement && current) {
        current.replaceWith(replacement);
        swapped = true;
      }
    }
    setOpen(open);
    if (bump) {
      document.querySelectorAll('.zp-cart__link, .zp-cart__total').forEach((element) => element.classList.add('zp-cart--bump'));
    }
    return swapped;
  };

  const sectionIds = () => [headerId(), pageId()].filter(Boolean);

  const change = async (line, quantity) => {
    const block = cart() || document.querySelector('[data-zp-cart-page]');
    if (!block) return;
    const changeUrl = block.dataset.changeUrl;
    document.querySelectorAll('[data-zp-cart], [data-zp-cart-page]').forEach((element) => element.setAttribute('aria-busy', 'true'));
    try {
      const response = await fetch(`${changeUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: Number(line),
          quantity: Number(quantity),
          sections: sectionIds(),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        // Shopify said no (usually "only N in stock"): stay put, redraw, and say why.
        await refresh();
        notice(data.description || data.message);
        return;
      }
      if (!data.sections || !render(data.sections)) window.location.reload();
    } catch (error) {
      // The network failed: fall back to the plain link, which goes through the cart page.
      window.location.href = `${changeUrl}?line=${line}&quantity=${quantity}`;
    }
  };

  // A short red line in the mini cart and on the cart page (e.g. not enough stock).
  const notice = (message) => {
    if (!message) return;
    document.querySelectorAll('[data-zp-cart-notice]').forEach((element) => {
      element.textContent = message;
      element.hidden = false;
    });
  };

  const refresh = async (options = {}) => {
    const ids = sectionIds();
    if (!ids.length) return;
    try {
      const response = await fetch(`${window.location.pathname}?sections=${ids.map(encodeURIComponent).join(',')}`);
      render(await response.json(), options);
    } catch (error) {
      window.location.reload();
    }
  };

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-zp-cart-toggle]')) {
      setOpen(!open);
      return;
    }
    if (event.target.closest('[data-zp-cart-close]')) {
      setOpen(false);
      toggle()?.focus();
      return;
    }
    const step = event.target.closest('[data-zp-cart-line]');
    if (step) {
      event.preventDefault();
      change(step.dataset.zpCartLine, step.dataset.zpCartQty);
      return;
    }
    // A click anywhere outside the total and its mini cart closes it.
    if (open && !cart()?.contains(event.target)) setOpen(false);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && open) {
      setOpen(false);
      toggle()?.focus();
    }
  });

  document.addEventListener('zp:cart-change', (event) => {
    if (event.detail?.open) open = true;
    refresh({ bump: Boolean(event.detail?.bump) });
  });

  // Coming back with the browser's back button can show the page exactly as
  // it was left (scroll position and all), including an out-of-date count.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh();
  });
})();
